import { useState, useRef, useEffect, useCallback } from 'react';
import type { LocationSwitcherProps } from './types';

export function LocationSwitcher({
  locations,
  activeId,
  onChange,
  showAll = false,
  allLabel = 'All Locations',
}: LocationSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Single location — render static text, no dropdown
  if (locations.length <= 1 && !showAll) {
    const loc = locations[0];
    if (!loc) return null;
    return (
      <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
        {loc.name}
      </span>
    );
  }

  const activeName = activeId === 'all'
    ? allLabel
    : locations.find((l) => l.id === activeId)?.name ?? '';

  // Build option list: "All Locations" first (if showAll), then individual locations
  const options: Array<{ id: string | 'all'; name: string; code?: string }> = [];
  if (showAll) {
    options.push({ id: 'all', name: allLabel });
  }
  for (const loc of locations) {
    options.push({ id: loc.id, name: loc.name, code: loc.code });
  }

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Focus first option when opening
  useEffect(() => {
    if (open) {
      const activeIdx = options.findIndex((o) => o.id === activeId);
      setFocusedIndex(activeIdx >= 0 ? activeIdx : 0);
    }
  }, [open]);

  const selectOption = useCallback(
    (id: string | 'all') => {
      onChange(id);
      setOpen(false);
      triggerRef.current?.focus();
    },
    [onChange],
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!open) {
        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setOpen(true);
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((prev) => Math.min(prev + 1, options.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Home':
          e.preventDefault();
          setFocusedIndex(0);
          break;
        case 'End':
          e.preventDefault();
          setFocusedIndex(options.length - 1);
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < options.length) {
            selectOption(options[focusedIndex].id);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setOpen(false);
          triggerRef.current?.focus();
          break;
      }
    },
    [open, focusedIndex, options, selectOption],
  );

  // Scroll focused option into view
  useEffect(() => {
    if (!open || focusedIndex < 0) return;
    const list = listRef.current;
    if (!list) return;
    const item = list.children[focusedIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  }, [focusedIndex, open]);

  return (
    <div ref={containerRef} className="relative" onKeyDown={handleKeyDown}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={[
          'flex h-8 items-center gap-1 rounded border border-neutral-200',
          'px-3 text-sm text-neutral-700',
          'hover:bg-neutral-100 dark:border-neutral-700',
          'dark:text-neutral-300 dark:hover:bg-neutral-800',
          'transition-colors duration-150',
        ].join(' ')}
        aria-label="Select location"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="max-w-[140px] truncate">{activeName}</span>
        {/* TODO: replace with icon library */}
        <span
          aria-hidden="true"
          className={[
            'text-xs transition-transform duration-150',
            open ? 'rotate-180' : '',
          ].join(' ')}
        >
          ▾
        </span>
      </button>

      {open && (
        <ul
          ref={listRef}
          role="listbox"
          aria-label="Locations"
          className={[
            'absolute right-0 top-full z-dropdown mt-1 w-52',
            'max-h-64 overflow-y-auto',
            'rounded-md border border-neutral-200 bg-white py-1 shadow-md',
            'dark:border-neutral-700 dark:bg-neutral-800',
          ].join(' ')}
        >
          {options.map((opt, i) => {
            const isActive = activeId === opt.id;
            const isFocused = focusedIndex === i;
            const isAllOption = opt.id === 'all';

            return (
              <li key={opt.id}>
                {/* Divider between "All Locations" and individual locations */}
                {isAllOption && i === 0 && options.length > 1 && null}
                <div
                  role="option"
                  aria-selected={isActive}
                  tabIndex={-1}
                  className={[
                    'flex cursor-pointer items-center gap-2 px-3 py-2 text-sm',
                    'transition-colors duration-100',
                    isActive
                      ? 'bg-brand-50 font-medium text-brand-700 dark:bg-brand-900/20 dark:text-brand-300'
                      : isFocused
                        ? 'bg-neutral-100 text-neutral-900 dark:bg-neutral-700 dark:text-neutral-100'
                        : 'text-neutral-700 dark:text-neutral-300',
                  ].join(' ')}
                  onClick={() => selectOption(opt.id)}
                  onMouseEnter={() => setFocusedIndex(i)}
                >
                  {isAllOption && (
                    // TODO: replace with icon library
                    <span className="text-sm" aria-hidden="true">🌐</span>
                  )}
                  <div className="min-w-0 flex-1">
                    {opt.code && (
                      <span className="mr-1.5 font-mono text-xs text-neutral-400">
                        {opt.code}
                      </span>
                    )}
                    <span className="truncate">{opt.name}</span>
                  </div>
                  {isActive && (
                    // TODO: replace with icon library
                    <span className="text-brand-500 text-xs" aria-hidden="true">✓</span>
                  )}
                </div>
                {/* Divider after "All Locations" */}
                {isAllOption && options.length > 1 && (
                  <div className="mx-2 my-1 border-t border-neutral-200 dark:border-neutral-700" />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
